import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
    Building2, 
    Upload, 
    Save, 
    Phone, 
    Mail, 
    Globe, 
    MapPin,
    FileText,
    Image as ImageIcon,
    Loader2,
    Check
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const EmpresaPage = () => {
    const [empresa, setEmpresa] = useState({
        nombre: '',
        nombre_comercial: '',
        rnc: '',
        direccion: '',
        ciudad: '',
        telefono: '',
        telefono2: '',
        email: '',
        website: '',
        slogan: '',
        horario: '',
        mensaje_recibo: '',
        mensaje_contrato: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [logoUrl, setLogoUrl] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        fetchEmpresa();
        fetchLogo();
    }, []);

    const fetchEmpresa = async () => {
        try {
            const data = await api.get('/empresa');
            setEmpresa(data);
        } catch (error) {
            console.error('Error fetching empresa:', error);
            toast.error('Error al cargar datos de la empresa');
        } finally {
            setLoading(false);
        }
    };

    const fetchLogo = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/empresa/logo`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (response.ok) {
                const blob = await response.blob();
                setLogoUrl(URL.createObjectURL(blob));
            }
        } catch (error) {
            console.log('No logo found or error fetching logo');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEmpresa(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put('/empresa', empresa);
            toast.success('Datos de la empresa actualizados');
        } catch (error) {
            console.error('Error saving empresa:', error);
            toast.error('Error al guardar los datos');
        } finally {
            setSaving(false);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Solo se permiten archivos de imagen');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error('El archivo es demasiado grande (máximo 5MB)');
            return;
        }

        setUploadingLogo(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/empresa/logo`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: formData
            });

            if (response.ok) {
                toast.success('Logo subido exitosamente');
                fetchLogo();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Error al subir el logo');
            }
        } catch (error) {
            console.error('Error uploading logo:', error);
            toast.error('Error al subir el logo');
        } finally {
            setUploadingLogo(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="empresa-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-heading font-bold">Perfil de la Empresa</h1>
                    <p className="text-muted-foreground">
                        Configure la información de su empresa que aparecerá en documentos
                    </p>
                </div>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
                {/* Logo Card */}
                <Card className="md:col-span-1">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <ImageIcon className="w-5 h-5" />
                            Logo de la Empresa
                        </CardTitle>
                        <CardDescription>
                            Aparecerá en contratos, recibos y pagarés
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div 
                            className="w-full aspect-square border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/30 overflow-hidden"
                            data-testid="logo-preview"
                        >
                            {logoUrl ? (
                                <img 
                                    src={logoUrl} 
                                    alt="Logo de la empresa" 
                                    className="w-full h-full object-contain p-4"
                                />
                            ) : (
                                <div className="text-center p-4">
                                    <Building2 className="w-16 h-16 mx-auto text-muted-foreground/50" />
                                    <p className="text-sm text-muted-foreground mt-2">
                                        Sin logo
                                    </p>
                                </div>
                            )}
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                            data-testid="logo-input"
                        />
                        <Button 
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingLogo}
                            className="w-full"
                            variant="outline"
                            data-testid="upload-logo-btn"
                        >
                            {uploadingLogo ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Subiendo...
                                </>
                            ) : (
                                <>
                                    <Upload className="w-4 h-4 mr-2" />
                                    {logoUrl ? 'Cambiar Logo' : 'Subir Logo'}
                                </>
                            )}
                        </Button>
                        <p className="text-xs text-muted-foreground text-center">
                            PNG, JPG o WEBP. Máximo 5MB.
                        </p>
                    </CardContent>
                </Card>

                {/* Form Card */}
                <Card className="md:col-span-2">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <FileText className="w-5 h-5" />
                            Información General
                        </CardTitle>
                        <CardDescription>
                            Estos datos se usarán en los documentos generados
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Identificación */}
                            <div className="grid gap-4 md:grid-cols-2">
                                <div className="space-y-2">
                                    <Label htmlFor="nombre">Razón Social *</Label>
                                    <Input
                                        id="nombre"
                                        name="nombre"
                                        value={empresa.nombre}
                                        onChange={handleChange}
                                        placeholder="Nombre legal de la empresa"
                                        data-testid="empresa-nombre"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="nombre_comercial">Nombre Comercial</Label>
                                    <Input
                                        id="nombre_comercial"
                                        name="nombre_comercial"
                                        value={empresa.nombre_comercial}
                                        onChange={handleChange}
                                        placeholder="Nombre para documentos"
                                        data-testid="empresa-nombre-comercial"
                                    />
                                </div>
                            </div>

                            <div className="grid gap-4 md:grid-cols-2">
                                <div className="space-y-2">
                                    <Label htmlFor="rnc">RNC / NIT</Label>
                                    <Input
                                        id="rnc"
                                        name="rnc"
                                        value={empresa.rnc}
                                        onChange={handleChange}
                                        placeholder="Ej: 123-456789-0"
                                        data-testid="empresa-rnc"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="slogan">Slogan</Label>
                                    <Input
                                        id="slogan"
                                        name="slogan"
                                        value={empresa.slogan}
                                        onChange={handleChange}
                                        placeholder="Lema de la empresa"
                                        data-testid="empresa-slogan"
                                    />
                                </div>
                            </div>

                            {/* Ubicación */}
                            <div className="pt-4 border-t">
                                <h3 className="font-medium flex items-center gap-2 mb-4">
                                    <MapPin className="w-4 h-4" />
                                    Ubicación
                                </h3>
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div className="space-y-2 md:col-span-2">
                                        <Label htmlFor="direccion">Dirección</Label>
                                        <Input
                                            id="direccion"
                                            name="direccion"
                                            value={empresa.direccion}
                                            onChange={handleChange}
                                            placeholder="Calle, número, sector"
                                            data-testid="empresa-direccion"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="ciudad">Ciudad</Label>
                                        <Input
                                            id="ciudad"
                                            name="ciudad"
                                            value={empresa.ciudad}
                                            onChange={handleChange}
                                            placeholder="Ciudad o provincia"
                                            data-testid="empresa-ciudad"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="horario">Horario de Atención</Label>
                                        <Input
                                            id="horario"
                                            name="horario"
                                            value={empresa.horario}
                                            onChange={handleChange}
                                            placeholder="Lun-Vie 8:00-5:00"
                                            data-testid="empresa-horario"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Contacto */}
                            <div className="pt-4 border-t">
                                <h3 className="font-medium flex items-center gap-2 mb-4">
                                    <Phone className="w-4 h-4" />
                                    Contacto
                                </h3>
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="telefono">Teléfono Principal</Label>
                                        <Input
                                            id="telefono"
                                            name="telefono"
                                            value={empresa.telefono}
                                            onChange={handleChange}
                                            placeholder="809-555-1234"
                                            data-testid="empresa-telefono"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="telefono2">Teléfono Secundario</Label>
                                        <Input
                                            id="telefono2"
                                            name="telefono2"
                                            value={empresa.telefono2}
                                            onChange={handleChange}
                                            placeholder="809-555-5678"
                                            data-testid="empresa-telefono2"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Correo Electrónico</Label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                            <Input
                                                id="email"
                                                name="email"
                                                type="email"
                                                value={empresa.email}
                                                onChange={handleChange}
                                                placeholder="info@empresa.com"
                                                className="pl-10"
                                                data-testid="empresa-email"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="website">Sitio Web</Label>
                                        <div className="relative">
                                            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                            <Input
                                                id="website"
                                                name="website"
                                                value={empresa.website}
                                                onChange={handleChange}
                                                placeholder="www.empresa.com"
                                                className="pl-10"
                                                data-testid="empresa-website"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Mensajes para documentos */}
                            <div className="pt-4 border-t">
                                <h3 className="font-medium flex items-center gap-2 mb-4">
                                    <FileText className="w-4 h-4" />
                                    Mensajes para Documentos
                                </h3>
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="mensaje_recibo">Mensaje en Recibos</Label>
                                        <Textarea
                                            id="mensaje_recibo"
                                            name="mensaje_recibo"
                                            value={empresa.mensaje_recibo}
                                            onChange={handleChange}
                                            placeholder="Gracias por su pago. Estamos para servirle."
                                            rows={2}
                                            data-testid="empresa-mensaje-recibo"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="mensaje_contrato">Mensaje en Contratos</Label>
                                        <Textarea
                                            id="mensaje_contrato"
                                            name="mensaje_contrato"
                                            value={empresa.mensaje_contrato}
                                            onChange={handleChange}
                                            placeholder="Mensaje adicional para contratos"
                                            rows={2}
                                            data-testid="empresa-mensaje-contrato"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end pt-4">
                                <Button 
                                    type="submit" 
                                    disabled={saving}
                                    data-testid="save-empresa-btn"
                                >
                                    {saving ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Guardando...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4 mr-2" />
                                            Guardar Cambios
                                        </>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>

            {/* Preview Card */}
            <Card>
                <CardHeader>
                    <CardTitle>Vista Previa</CardTitle>
                    <CardDescription>
                        Así aparecerá la información en los documentos generados
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="p-6 border rounded-lg bg-white">
                        <div className="flex items-start gap-4">
                            {logoUrl && (
                                <img 
                                    src={logoUrl} 
                                    alt="Logo" 
                                    className="w-16 h-16 object-contain"
                                />
                            )}
                            <div className="flex-1">
                                <h3 className="font-bold text-lg">
                                    {empresa.nombre_comercial || empresa.nombre || 'Nombre de la Empresa'}
                                </h3>
                                {empresa.rnc && (
                                    <p className="text-sm text-muted-foreground">RNC: {empresa.rnc}</p>
                                )}
                                {empresa.direccion && (
                                    <p className="text-sm text-muted-foreground">
                                        {empresa.direccion}{empresa.ciudad && `, ${empresa.ciudad}`}
                                    </p>
                                )}
                                {(empresa.telefono || empresa.telefono2) && (
                                    <p className="text-sm text-muted-foreground">
                                        Tel: {[empresa.telefono, empresa.telefono2].filter(Boolean).join(' / ')}
                                    </p>
                                )}
                                {(empresa.email || empresa.website) && (
                                    <p className="text-sm text-muted-foreground">
                                        {[empresa.email, empresa.website].filter(Boolean).join(' | ')}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="mt-4 pt-4 border-t">
                            <div className="h-px bg-gray-200"></div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default EmpresaPage;
